import React from 'react';
import { Box, Link, Typography, useMediaQuery } from '@mui/material';
import styled from '@emotion/styled';
import { billingTypeAbbr, formatMinutesAsHours } from '../utils/timeUtils';

const StyledTable = styled('table')(({ theme }) => ({
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: theme.typography.size.md,
}));

const StyledTh = styled('th')(({ theme }) => ({
    textAlign: 'right',
    padding: theme.spacing(0.5, 1),
    color: theme.palette.text.secondary,
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: theme.typography.size.md,
    '&:first-of-type': { textAlign: 'left' },
}));

const StyledTd = styled('td')(({ theme }) => ({
    textAlign: 'right',
    padding: theme.spacing(0.75, 1),
    '&:first-of-type': { textAlign: 'left' },
}));

const StyledTr = styled('tr')(({ theme }) => ({
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-of-type': { borderBottom: 'none' },
}));

const RowLink = styled(Link)(({ theme }) => ({
    color: theme.palette.primary.main,
    textDecoration: 'none',
    cursor: 'pointer',
    '&:hover': { textDecoration: 'underline' },
}));

const StackedRow = styled(Box)(({ theme }) => ({
    padding: theme.spacing(1.25, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
}));

const StackedTotalsRow = styled(Box)(({ theme }) => ({
    padding: theme.spacing(1.25, 0),
}));

const StackedHoursRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.5),
}));

const LegendText = styled(Typography)(({ theme }) => ({
    display: 'block',
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
}));

const HoursPill = styled(Box, { shouldForwardProp: (p) => p !== 'positive' })(({ theme, positive }) => ({
    display: 'inline-flex',
    alignItems: 'baseline',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.25, 1),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: positive ? theme.palette.success.light : theme.palette.action.hover,
    color: positive ? theme.palette.success.contrastText : theme.palette.text.secondary,
    fontSize: theme.typography.size.smd,
    lineHeight: 1.4,
}));

export const deriveHoursColumns = (rows, hideEmptyColumns = false) => {
    const totals = {};
    (rows || []).forEach((row) => {
        for (const [type, minutes] of Object.entries(row.time || {})) {
            totals[type] = (totals[type] || 0) + (minutes || 0);
        }
    });
    const types = Object.keys(totals)
        .filter((type) => !hideEmptyColumns || totals[type] > 0)
        .sort();
    return { types, totals };
};

const HoursByTypeTable = ({ rows, labelHeading, hideEmptyColumns = false }) => {
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
    const safeRows = rows || [];
    const { types: derivedTypes, totals } = deriveHoursColumns(safeRows, hideEmptyColumns);
    const types = derivedTypes.length > 0 ? derivedTypes : ['Hourly'];
    const legend = types.filter((type) => billingTypeAbbr(type) !== type).map((type) => `${billingTypeAbbr(type)} = ${type}`).join(', ');

    const renderTable = () => (
        <StyledTable>
            <thead>
                <StyledTr>
                    <StyledTh>{labelHeading}</StyledTh>
                    {types.map((type) => (
                        <StyledTh key={type}>{billingTypeAbbr(type)}</StyledTh>
                    ))}
                </StyledTr>
            </thead>
            <tbody>
                {safeRows.map((row) => (
                    <StyledTr key={row.key}>
                        <StyledTd>{row.onClick ? <RowLink onClick={row.onClick}>{row.label}</RowLink> : row.label}</StyledTd>
                        {types.map((type) => {
                            const minutes = row.time?.[type] || 0;
                            return (
                                <StyledTd key={type}>
                                    <Typography variant="body2" component="span" color={minutes > 0 ? 'success.main' : 'text.primary'}>
                                        {formatMinutesAsHours(minutes)}
                                    </Typography>
                                </StyledTd>
                            );
                        })}
                    </StyledTr>
                ))}
                <StyledTr>
                    <StyledTd>
                        <Typography variant="body2" fontWeight="medium">Total:</Typography>
                    </StyledTd>
                    {types.map((type) => {
                        const minutes = totals[type] || 0;
                        return (
                            <StyledTd key={type}>
                                <Typography variant="body2" component="span" fontWeight="medium" color={minutes > 0 ? 'success.main' : 'text.primary'}>
                                    {formatMinutesAsHours(minutes)}
                                </Typography>
                            </StyledTd>
                        );
                    })}
                </StyledTr>
            </tbody>
        </StyledTable>
    );

    const renderStacked = () => (
        <Box>
            {safeRows.map((row) => {
                const positiveTypes = types.filter((type) => (row.time?.[type] || 0) > 0);
                const typesToShow = positiveTypes.length > 0 ? positiveTypes : types;
                return (
                    <StackedRow key={row.key}>
                        {row.onClick ? <RowLink onClick={row.onClick}>{row.label}</RowLink> : <Typography variant="body2">{row.label}</Typography>}
                        <StackedHoursRow>
                            {typesToShow.map((type) => {
                                const minutes = row.time?.[type] || 0;
                                return (
                                    <HoursPill key={type} positive={minutes > 0}>
                                        <Box component="span" fontWeight="medium">{billingTypeAbbr(type)}</Box>
                                        <Box component="span">{formatMinutesAsHours(minutes)}</Box>
                                    </HoursPill>
                                );
                            })}
                        </StackedHoursRow>
                    </StackedRow>
                );
            })}
            <StackedTotalsRow>
                <Typography variant="body2" fontWeight="medium">Total:</Typography>
                <StackedHoursRow>
                    {types.map((type) => {
                        const minutes = totals[type] || 0;
                        return (
                            <HoursPill key={type} positive={minutes > 0}>
                                <Box component="span" fontWeight="medium">{billingTypeAbbr(type)}</Box>
                                <Box component="span">{formatMinutesAsHours(minutes)}</Box>
                            </HoursPill>
                        );
                    })}
                </StackedHoursRow>
            </StackedTotalsRow>
        </Box>
    );

    return (
        <>
            {isMobile ? renderStacked() : renderTable()}
            {legend && <LegendText variant="caption">{legend}</LegendText>}
        </>
    );
};

export default HoursByTypeTable;
